import { useEffect, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Tag,
  message,
  Row,
  Col,
  Checkbox,
  Divider,
  Tabs,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  CarOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { confirmDelete, confirmStatusChange } from '../../hooks/useConfirm'
import { deliveryAPI } from '../../api'

const { Option } = Select
const CheckboxGroup = Checkbox.Group

// 广东省省市县三级数据
const GUANGDONG_REGIONS = [
  {
    province: '广东省',
    cities: [
      { name: '广州市', districts: ['越秀区', '海珠区', '荔湾区', '天河区', '白云区', '黄埔区', '番禺区', '花都区', '南沙区', '从化区', '增城区'] },
      { name: '深圳市', districts: ['罗湖区', '福田区', '南山区', '宝安区', '龙岗区', '盐田区', '龙华区', '坪山区', '光明区', '大鹏新区'] },
      { name: '珠海市', districts: ['香洲区', '斗门区', '金湾区'] },
      { name: '汕头市', districts: ['金平区', '龙湖区', '濠江区', '潮阳区', '潮南区', '澄海区', '南澳县'] },
      { name: '佛山市', districts: ['禅城区', '南海区', '顺德区', '三水区', '高明区'] },
      { name: '韶关市', districts: ['武江区', '浈江区', '曲江区', '始兴县', '仁化县', '翁源县', '乳源瑶族自治县', '新丰县', '乐昌市', '南雄市'] },
      { name: '湛江市', districts: ['赤坎区', '霞山区', '坡头区', '麻章区', '遂溪县', '徐闻县', '廉江市', '雷州市', '吴川市'] },
      { name: '肇庆市', districts: ['端州区', '鼎湖区', '高要区', '广宁县', '怀集县', '封开县', '德庆县', '四会市'] },
      { name: '江门市', districts: ['蓬江区', '江海区', '新会区', '台山市', '开平市', '鹤山市', '恩平市'] },
      { name: '茂名市', districts: ['茂南区', '电白区', '高州市', '化州市', '信宜市'] },
      { name: '惠州市', districts: ['惠城区', '惠阳区', '博罗县', '惠东县', '龙门县'] },
      { name: '梅州市', districts: ['梅江区', '梅县区', '大埔县', '丰顺县', '五华县', '平远县', '蕉岭县', '兴宁市'] },
      { name: '汕尾市', districts: ['城区', '海丰县', '陆河县', '陆丰市'] },
      { name: '河源市', districts: ['源城区', '紫金县', '龙川县', '连平县', '和平县', '东源县'] },
      { name: '阳江市', districts: ['江城区', '阳东区', '阳西县', '阳春市'] },
      { name: '清远市', districts: ['清城区', '清新区', '佛冈县', '阳山县', '连山壮族瑶族自治县', '连南瑶族自治县', '英德市', '连州市'] },
      { name: '东莞市', districts: ['莞城区', '东城区', '南城区', '万江区', '石龙镇', '虎门镇', '长安镇', '厚街镇', '寮步镇', '大朗镇', '樟木头镇', '塘厦镇', '凤岗镇', '常平镇'] },
      { name: '中山市', districts: ['石岐区', '东区', '西区', '南区', '五桂山区', '火炬开发区', '小榄镇', '古镇镇', '横栏镇', '东升镇', '港口镇', '沙溪镇', '大涌镇', '黄圃镇', '南头镇', '东凤镇', '阜沙镇', '三角镇', '民众镇', '南朗镇', '三乡镇', '坦洲镇', '板芙镇', '神湾镇'] },
      { name: '潮州市', districts: ['湘桥区', '潮安区', '饶平县'] },
      { name: '揭阳市', districts: ['榕城区', '揭东区', '揭西县', '惠来县', '普宁市'] },
      { name: '云浮市', districts: ['云城区', '云安区', '新兴县', '郁南县', '罗定市'] },
    ],
  },
]

// 星期选项
const WEEKDAYS = [
  { label: '周日', value: 0 },
  { label: '周一', value: 1 },
  { label: '周二', value: 2 },
  { label: '周三', value: 3 },
  { label: '周四', value: 4 },
  { label: '周五', value: 5 },
  { label: '周六', value: 6 },
]

interface DeliveryArea {
  id: number
  province: string
  cities: string[]
  districts: string[]
  deliveryDays: number[]
  cutOffTime: string
  leadTime: number
  status: boolean
  createdAt: string
}

interface FreightRule {
  id: number
  areaId: number
  areaName: string
  baseFreight: number
  freeThreshold: number
  status: boolean
}

const DeliveryConfig = () => {
  const [activeTab, setActiveTab] = useState('area')
  const [loading, setLoading] = useState(false)

  // 配送区域相关
  const [areas, setAreas] = useState<DeliveryArea[]>([])
  const [areaModalVisible, setAreaModalVisible] = useState(false)
  const [editingArea, setEditingArea] = useState<DeliveryArea | null>(null)
  const [areaForm] = Form.useForm()
  const [selectedCities, setSelectedCities] = useState<string[]>([])
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([])
  const [selectedDeliveryDays, setSelectedDeliveryDays] = useState<number[]>([])
  const [expandedCities, setExpandedCities] = useState<string[]>([])
  const [areaSubmitting, setAreaSubmitting] = useState(false)

  // 运费规则相关
  const [freightRules, setFreightRules] = useState<FreightRule[]>([])
  const [freightModalVisible, setFreightModalVisible] = useState(false)
  const [editingFreight, setEditingFreight] = useState<FreightRule | null>(null)
  const [freightForm] = Form.useForm()
  const [freightSubmitting, setFreightSubmitting] = useState(false)

  useEffect(() => {
    fetchAreas()
    fetchFreightRules()
  }, [])

  const fetchAreas = async () => {
    setLoading(true)
    try {
      const res = await deliveryAPI.getAreas()
      if (res.code === 200) {
        setAreas(res.data || [])
      }
    } catch (error) {
      // 错误由 request 拦截器统一处理
    } finally {
      setLoading(false)
    }
  }

  const fetchFreightRules = async () => {
    try {
      const res = await deliveryAPI.getFreightRules()
      if (res.code === 200) {
        setFreightRules(res.data || [])
      }
    } catch (error) {
      // 错误统一处理
    }
  }

  // 获取所有区县
  const getAllDistricts = () => {
    const allDistricts: string[] = []
    GUANGDONG_REGIONS[0].cities.forEach(city => {
      city.districts.forEach(district => {
        allDistricts.push(`${city.name}-${district}`)
      })
    })
    return allDistricts
  }

  // 获取配送日显示文本
  const getDeliveryDaysText = (days: number[]) => {
    if (!days || days.length === 0) return '-'
    const sorted = [...days].sort((a, b) => a - b)
    return sorted.map(d => WEEKDAYS[d].label).join('、')
  }

  // ========== 配送区域管理 ==========
  const handleAddArea = () => {
    setEditingArea(null)
    areaForm.resetFields()
    setSelectedCities([])
    setSelectedDistricts([])
    setSelectedDeliveryDays([])
    setExpandedCities([])
    areaForm.setFieldsValue({ province: '广东省', leadTime: 1, cutOffTime: '16:00', status: true })
    setAreaModalVisible(true)
  }

  const handleEditArea = (record: DeliveryArea) => {
    setEditingArea(record)
    areaForm.setFieldsValue(record)
    setSelectedCities(record.cities || [])
    setSelectedDistricts(record.districts || [])
    setSelectedDeliveryDays(record.deliveryDays || [])

    const citiesWithDistricts = Array.from(new Set((record.districts || []).map(d => d.split('-')[0])))
    setExpandedCities(citiesWithDistricts)

    setAreaModalVisible(true)
  }

  const handleDeleteArea = (record: DeliveryArea) => {
    const areaName = record.cities?.join('、') || `配送区域-${record.id}`
    confirmDelete(areaName, '配送管理', async () => {
      const res = await deliveryAPI.deleteArea(record.id)
      if (res.code === 200) {
        setAreas(areas.filter(a => a.id !== record.id))
        message.success('删除成功')
        fetchAreas()
      }
    })
  }

  const handleToggleAreaStatus = (record: DeliveryArea, status: boolean) => {
    const areaName = record.cities?.join('、') || `配送区域-${record.id}`
    confirmStatusChange(areaName, status, '配送管理', async () => {
      const res = await deliveryAPI.toggleAreaStatus(record.id, status)
      if (res.code === 200) {
        setAreas(areas.map(a => a.id === record.id ? { ...a, status } : a))
        message.success(status ? '已启用' : '已禁用')
      }
    })
  }

  const handleAreaSubmit = async () => {
    try {
      const values = await areaForm.validateFields()

      if (selectedDistricts.length === 0) {
        message.warning('请至少选择一个区县')
        return
      }
      if (selectedDeliveryDays.length === 0) {
        message.warning('请至少选择一个配送日')
        return
      }

      setAreaSubmitting(true)
      const areaData = {
        ...values,
        cities: selectedCities,
        districts: selectedDistricts,
        deliveryDays: selectedDeliveryDays,
      }

      if (editingArea) {
        const res = await deliveryAPI.updateArea(editingArea.id, areaData)
        if (res.code === 200) {
          setAreas(areas.map(a => a.id === editingArea.id ? res.data : a))
          message.success('更新成功')
        }
      } else {
        const res = await deliveryAPI.createArea(areaData)
        if (res.code === 200) {
          setAreas([...areas, res.data])
          message.success('添加成功')
        }
      }
      setAreaModalVisible(false)
      fetchAreas()
    } catch (error) {
      // 错误统一处理
    } finally {
      setAreaSubmitting(false)
    }
  }

  // ========== 运费规则管理 ==========
  const handleAddFreight = () => {
    const areasWithoutFreight = areas.filter(a => !freightRules.some(r => r.areaId === a.id))
    if (areasWithoutFreight.length === 0) {
      message.warning('所有配送区域都已设置运费规则')
      return
    }

    setEditingFreight(null)
    freightForm.resetFields()
    freightForm.setFieldsValue({ baseFreight: 10, freeThreshold: 0, status: true })
    setFreightModalVisible(true)
  }

  const handleEditFreight = (record: FreightRule) => {
    setEditingFreight(record)
    freightForm.setFieldsValue(record)
    setFreightModalVisible(true)
  }

  const handleDeleteFreight = (record: FreightRule) => {
    confirmDelete(record.areaName + ' 运费规则', '配送管理', async () => {
      const res = await deliveryAPI.deleteFreightRule(record.id)
      if (res.code === 200) {
        setFreightRules(freightRules.filter(r => r.id !== record.id))
        message.success('删除成功')
        fetchFreightRules()
      }
    })
  }

  const handleToggleFreightStatus = (record: FreightRule, status: boolean) => {
    confirmStatusChange(record.areaName + ' 运费规则', status, '配送管理', async () => {
      const res = await deliveryAPI.toggleFreightRuleStatus(record.id, status)
      if (res.code === 200) {
        setFreightRules(freightRules.map(r => r.id === record.id ? { ...r, status } : r))
        message.success(status ? '已启用' : '已禁用')
      }
    })
  }

  const handleFreightSubmit = async () => {
    try {
      const values = await freightForm.validateFields()

      setFreightSubmitting(true)
      const selectedArea = areas.find(a => a.id === values.areaId)
      const areaName = selectedArea?.cities?.join('、') || ''

      if (editingFreight) {
        const res = await deliveryAPI.updateFreightRule(editingFreight.id, { ...values, areaName })
        if (res.code === 200) {
          setFreightRules(freightRules.map(r => r.id === editingFreight.id ? res.data : r))
          message.success('更新成功')
        }
      } else {
        const res = await deliveryAPI.createFreightRule({ ...values, areaName })
        if (res.code === 200) {
          setFreightRules([...freightRules, res.data])
          message.success('添加成功')
        }
      }
      setFreightModalVisible(false)
      fetchFreightRules()
    } catch (error) {
      // 错误统一处理
    } finally {
      setFreightSubmitting(false)
    }
  }

  const getAvailableAreas = () => {
    if (editingFreight) {
      return areas.filter(a =>
        a.id === editingFreight.areaId || !freightRules.some(r => r.areaId === a.id)
      )
    }
    return areas.filter(a => !freightRules.some(r => r.areaId === a.id))
  }

  const areaColumns: ColumnsType<DeliveryArea> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '省份', dataIndex: 'province', key: 'province', width: 80 },
    {
      title: '开通区县',
      dataIndex: 'districts',
      key: 'districts',
      width: 300,
      render: (districts: string[]) => (
        <Space wrap>
          {districts?.slice(0, 5).map(district => (
            <Tag key={district} color="green" style={{ fontSize: 11 }}>
              {district}
            </Tag>
          ))}
          {districts?.length > 5 && (
            <Tag color="default">+{districts.length - 5}个</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '配送日',
      dataIndex: 'deliveryDays',
      key: 'deliveryDays',
      width: 150,
      render: (days: number[]) => getDeliveryDaysText(days),
    },
    {
      title: '截单时间',
      dataIndex: 'cutOffTime',
      key: 'cutOffTime',
      width: 90,
    },
    {
      title: '提前天数',
      dataIndex: 'leadTime',
      key: 'leadTime',
      width: 80,
      render: (val: number) => `${val}天`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (val: boolean, record: DeliveryArea) => (
        <Switch checked={val} size="small" onChange={(checked) => handleToggleAreaStatus(record, checked)} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: DeliveryArea) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditArea(record)}>
            编辑
          </Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteArea(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  const freightColumns: ColumnsType<FreightRule> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '配送区域', dataIndex: 'areaName', key: 'areaName', width: 200 },
    {
      title: '基础运费',
      dataIndex: 'baseFreight',
      key: 'baseFreight',
      width: 120,
      render: (val: number) => `¥${val.toFixed(2)}`,
    },
    {
      title: '免运费门槛',
      dataIndex: 'freeThreshold',
      key: 'freeThreshold',
      width: 150,
      render: (val: number) => val > 0 ? `满¥${val.toFixed(2)}免运费` : <Tag color="default">无免运费</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (val: boolean, record: FreightRule) => (
        <Switch checked={val} size="small" onChange={(checked) => handleToggleFreightStatus(record, checked)} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: FreightRule) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditFreight(record)}>
            编辑
          </Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteFreight(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="delivery-config-page">
      <Card size="small">
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          {
            label: <span><EnvironmentOutlined /> 开通区域</span>,
            key: 'area',
            children: (<>
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddArea}>
                添加配送区域
              </Button>
            </div>
            <Table
              columns={areaColumns}
              dataSource={areas}
              rowKey="id"
              loading={loading}
              pagination={false}
              size="small"
              scroll={{ x: 1100 }}
            />
            </>)
          },
          {
            label: <span><CarOutlined /> 运费规则</span>,
            key: 'freight',
            children: (<>
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddFreight}>
                添加运费规则
              </Button>
            </div>
            <Table
              columns={freightColumns}
              dataSource={freightRules}
              rowKey="id"
              loading={loading}
              pagination={false}
              size="small"
            />
            </>)
          }
        ]} />
      </Card>

      {/* 配送区域编辑弹窗 */}
      <Modal
        title={editingArea ? '编辑配送区域' : '添加配送区域'}
        open={areaModalVisible}
        onOk={handleAreaSubmit}
        onCancel={() => setAreaModalVisible(false)}
        confirmLoading={areaSubmitting}
        width={800}
        destroyOnClose
      >
        <Form form={areaForm} layout="vertical">
          <Form.Item name="province" label="省份" rules={[{ required: true }]}>
            <Select>
              <Option value="广东省">广东省</Option>
            </Select>
          </Form.Item>

          <Form.Item label="选择区县" required>
            <div style={{ marginBottom: 8 }}>
              <Space>
                <Checkbox
                  checked={selectedDistricts.length === getAllDistricts().length}
                  indeterminate={selectedDistricts.length > 0 && selectedDistricts.length < getAllDistricts().length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedDistricts(getAllDistricts())
                      setSelectedCities(GUANGDONG_REGIONS[0].cities.map(c => c.name))
                    } else {
                      setSelectedDistricts([])
                      setSelectedCities([])
                    }
                  }}
                >
                  全选所有区县
                </Checkbox>
                <Button type="link" size="small" onClick={() => {
                  setSelectedDistricts([])
                  setSelectedCities([])
                }}>
                  清空
                </Button>
              </Space>
            </div>

            <div style={{
              maxHeight: 400,
              overflowY: 'auto',
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              padding: 12,
            }}>
              {GUANGDONG_REGIONS[0].cities.map(city => {
                const cityDistricts = city.districts.map(d => `${city.name}-${d}`)
                const selectedCityDistricts = selectedDistricts.filter(d => d.startsWith(`${city.name}-`))
                const isCityFullySelected = cityDistricts.length === selectedCityDistricts.length && cityDistricts.length > 0
                const isCityPartiallySelected = selectedCityDistricts.length > 0 && !isCityFullySelected

                return (
                  <div key={city.name} style={{ marginBottom: 12 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 4,
                      padding: '4px 8px',
                      background: '#f5f5f5',
                      borderRadius: 4,
                    }}>
                      <Checkbox
                        checked={isCityFullySelected}
                        indeterminate={isCityPartiallySelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDistricts([...selectedDistricts, ...cityDistricts])
                            if (!selectedCities.includes(city.name)) {
                              setSelectedCities([...selectedCities, city.name])
                            }
                          } else {
                            setSelectedDistricts(selectedDistricts.filter(d => !d.startsWith(`${city.name}-`)))
                            setSelectedCities(selectedCities.filter(c => c !== city.name))
                          }
                        }}
                        style={{ marginRight: 8 }}
                      />
                      <span
                        style={{
                          flex: 1,
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          if (expandedCities.includes(city.name)) {
                            setExpandedCities(expandedCities.filter(c => c !== city.name))
                          } else {
                            setExpandedCities([...expandedCities, city.name])
                          }
                        }}
                      >
                        {city.name} ({cityDistricts.length}个区县)
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#999' }}>
                          {selectedCityDistricts.length}/{cityDistricts.length}
                        </span>
                      </span>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          if (expandedCities.includes(city.name)) {
                            setExpandedCities(expandedCities.filter(c => c !== city.name))
                          } else {
                            setExpandedCities([...expandedCities, city.name])
                          }
                        }}
                      >
                        {expandedCities.includes(city.name) ? '收起' : '展开'}
                      </Button>
                    </div>

                    {expandedCities.includes(city.name) && (
                      <div style={{ paddingLeft: 28, paddingTop: 4 }}>
                        <CheckboxGroup
                          value={selectedCityDistricts}
                          onChange={(values: any[]) => {
                            const otherDistricts = selectedDistricts.filter(d => !d.startsWith(`${city.name}-`))
                            const newSelectedDistricts = [...otherDistricts, ...values]
                            setSelectedDistricts(newSelectedDistricts)

                            const hasDistrict = values.length > 0
                            if (hasDistrict && !selectedCities.includes(city.name)) {
                              setSelectedCities([...selectedCities, city.name])
                            } else if (!hasDistrict && selectedCities.includes(city.name)) {
                              setSelectedCities(selectedCities.filter(c => c !== city.name))
                            }
                          }}
                        >
                          <Row gutter={[8, 8]}>
                            {city.districts.map(district => (
                              <Col span={8} key={district}>
                                <Checkbox value={`${city.name}-${district}`}>
                                  {district}
                                </Checkbox>
                              </Col>
                            ))}
                          </Row>
                        </CheckboxGroup>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
              已选择 {selectedDistricts.length} 个区县，覆盖 {selectedCities.length} 个城市
            </div>
          </Form.Item>

          <Divider />

          <Form.Item label="选择配送日" required>
            <div style={{ marginBottom: 8 }}>
              <Checkbox
                checked={selectedDeliveryDays.length === 7}
                indeterminate={selectedDeliveryDays.length > 0 && selectedDeliveryDays.length < 7}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedDeliveryDays([0, 1, 2, 3, 4, 5, 6])
                  } else {
                    setSelectedDeliveryDays([])
                  }
                }}
              >
                全选
              </Checkbox>
              <Button type="link" size="small" onClick={() => setSelectedDeliveryDays([])}>
                清空
              </Button>
            </div>
            <CheckboxGroup
              value={selectedDeliveryDays}
              onChange={(values: any[]) => setSelectedDeliveryDays(values as number[])}
            >
              <Space wrap>
                {WEEKDAYS.map(day => (
                  <Checkbox key={day.value} value={day.value}>{day.label}</Checkbox>
                ))}
              </Space>
            </CheckboxGroup>
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="cutOffTime" label="截单时间" rules={[{ required: true }]}>
                <Select placeholder="请选择截单时间">
                  <Option value="12:00">12:00</Option>
                  <Option value="14:00">14:00</Option>
                  <Option value="15:00">15:00</Option>
                  <Option value="16:00">16:00</Option>
                  <Option value="17:00">17:00</Option>
                  <Option value="18:00">18:00</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="leadTime" label="提前天数" rules={[{ required: true }]}>
                <InputNumber min={0} max={7} style={{ width: '100%' }} addonAfter="天" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="状态" valuePropName="checked">
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{
            padding: '8px 12px',
            background: '#f5f5f5',
            borderRadius: 4,
            fontSize: 12,
            color: '#666'
          }}>
            提示：截单时间前下单可在次日配送，提前天数指需要提前多少天下单
          </div>
        </Form>
      </Modal>

      {/* 运费规则编辑弹窗 */}
      <Modal
        title={editingFreight ? '编辑运费规则' : '添加运费规则'}
        open={freightModalVisible}
        onOk={handleFreightSubmit}
        onCancel={() => setFreightModalVisible(false)}
        confirmLoading={freightSubmitting}
        width={500}
        destroyOnClose
      >
        <Form form={freightForm} layout="vertical">
          <Form.Item name="areaId" label="配送区域" rules={[{ required: true, message: '请选择配送区域' }]}>
            <Select placeholder="请选择配送区域" disabled={!!editingFreight}>
              {getAvailableAreas().map(area => (
                <Option key={area.id} value={area.id}>
                  {area.cities?.join('、')} (配送日: {getDeliveryDaysText(area.deliveryDays)})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="baseFreight" label="基础运费" rules={[{ required: true, message: '请输入基础运费' }]}>
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  prefix="¥"
                  placeholder="基础运费"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="freeThreshold" label="免运费门槛" help="0表示无免运费">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  prefix="¥"
                  placeholder="满额免运费"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="status" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default DeliveryConfig
